<template>
    <div class="voiceai-dashboard">
        <div class="topbar">
            <div class="brand">
                <div class="brand-text">
                    <div class="crumb">VOICE AI USAGE</div>
                    <div class="store">
                        <button v-if="view === 'store'" class="backBtn" @click="goOverview">‹ Back</button>
                        <div class="store-main">
                            <span class="store-name">{{ view === 'overview' ? 'Dashboard' : currentStoreName }}</span>
                            <span class="srcTag" :class="dataLive ? 'live' : 'demo'">{{ dataLive ? '● Live' : 'No data' }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="va-content">
            <div v-if="error" class="banner err">Failed to load data.<br>{{ error }}</div>
            <div v-if="loading" class="banner loading">Loading…</div>

            <div class="rangeRow">
                <div class="tabs">
                    <button
                        v-for="r in ranges"
                        :key="r.id"
                        class="tab"
                        :class="{ on: range === r.id }"
                        @click="setRange(r.id)"
                    >{{ r.label }}</button>
                </div>
                <input v-if="range === 'day'" v-model="pickDate" type="date" class="date" @change="reload" />
                <div class="cmpSel">
                    Compare
                    <select v-model="cmp" @change="reload">
                        <option value="none">None</option>
                        <option value="prev">Previous period</option>
                    </select>
                </div>
            </div>

            <!-- Overview -->
            <template v-if="view === 'overview' && ov">
                <div class="kpiGrid">
                    <div class="kpi big">
                        <div class="kpiLabel">Voice AI calls</div>
                        <div class="kpiVal indigo">
                            {{ n(ov.tot.sent) }}
                            <span v-if="cmp !== 'none' && ov.prev" class="delta" :class="deltaClass(ov.tot.sent, ov.prev.sent)">{{ deltaText(ov.tot.sent, ov.prev.sent) }}</span>
                        </div>
                        <div class="kpiSub">{{ ov.stores.length }} stores</div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">AI completed</div>
                        <div class="kpiVal ok">
                            {{ n(ov.tot.success) }}
                            <span v-if="cmp !== 'none' && ov.prev" class="delta" :class="deltaClass(ov.tot.success, ov.prev.success)">{{ deltaText(ov.tot.success, ov.prev.success) }}</span>
                        </div>
                        <div class="kpiSub">{{ pct(ov.tot.success, ov.tot.sent) }} resolution</div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">Transferred</div>
                        <div class="kpiVal warn">
                            {{ n(ov.tot.transfer) }}
                            <span v-if="cmp !== 'none' && ov.prev" class="delta" :class="deltaClass(ov.tot.transfer, ov.prev.transfer)">{{ deltaText(ov.tot.transfer, ov.prev.transfer) }}</span>
                        </div>
                        <div class="kpiSub">{{ pct(ov.tot.transfer, ov.tot.sent) }} of total</div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">Total minutes</div>
                        <div class="kpiVal sky">
                            {{ n(ov.tot.minutes) }}<span class="unit">min</span>
                            <span v-if="cmp !== 'none' && ov.prev" class="delta" :class="deltaClass(ov.tot.minutes, ov.prev.minutes)">{{ deltaText(ov.tot.minutes, ov.prev.minutes) }}</span>
                        </div>
                        <div class="kpiSub">Avg {{ ov.tot.success ? (ov.tot.minutes / ov.tot.success).toFixed(1) : '0' }} min/call</div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panelHead">
                        <h3>Call trend (all stores)</h3>
                        <span class="hint">Daily · count · minutes</span>
                    </div>
                    <div v-if="ov.trend && ov.trend.length" class="chart">
                        <div v-for="b in ov.trend" :key="b.date" class="col">
                            <span class="barNum">{{ b.sent }}<small>·{{ b.minutes }}m</small></span>
                            <div class="stack" :style="{ height: barHeight(b.sent, ovTrendMax) + 'px' }">
                                <i class="seg ok" :style="{ height: segPct(b.success - b.transfer, b.sent) }">
                                    <em v-if="b.success - b.transfer > 0">{{ b.success - b.transfer }}</em>
                                </i>
                                <i class="seg warn" :style="{ height: segPct(b.transfer, b.sent) }">
                                    <em v-if="b.transfer > 0">{{ b.transfer }}</em>
                                </i>
                            </div>
                            <label>{{ b.date.slice(5) }}</label>
                        </div>
                    </div>
                    <div v-else class="empty">No trend data.</div>
                    <div class="legend">
                        <span><b class="okDot"></b>AI completed</span>
                        <span><b class="warnDot"></b>Transferred</span>
                    </div>
                </div>

                <div class="panel">
                    <div class="panelHead">
                        <h3>Store breakdown</h3>
                        <span class="hint">Click a store to open detail</span>
                    </div>
                    <table v-if="ov.stores.length">
                        <thead>
                            <tr>
                                <th>Store</th>
                                <th>Calls</th>
                                <th>Success</th>
                                <th>Transfer</th>
                                <th>Minutes</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="s in ov.stores" :key="s.name" class="storeRow" @click="openStore(s)">
                                <td><span class="caret">›</span>{{ s.name }}</td>
                                <td>{{ n(s.sent) }}</td>
                                <td>{{ n(s.success) }}<span class="pct">{{ pct(s.success, s.sent) }}</span></td>
                                <td>{{ n(s.transfer) }}</td>
                                <td>{{ n(s.minutes) }}</td>
                                <td><span class="drill">View ›</span></td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="empty">No calls in this period.</div>
                </div>
            </template>

            <!-- Store detail -->
            <template v-else-if="view === 'store' && st">
                <div v-if="range === 'today' && st.live && st.live.length" class="liveCard">
                    <div class="liveHead">
                        <span class="dot"></span>
                        <span class="lt">Active calls</span>
                        <span class="lc">{{ st.live.length }} in progress</span>
                    </div>
                    <div v-for="(c, i) in st.live" :key="i" class="liveItem">
                        <span class="g">●</span>
                        <span>{{ c.title || 'In call' }}</span>
                        <span class="el">{{ Math.round((c.durationSecs || 0) / 60) }} min</span>
                    </div>
                </div>

                <div class="kpiGrid">
                    <div class="kpi big">
                        <div class="kpiLabel">Voice AI calls</div>
                        <div class="kpiVal indigo">
                            {{ n(st.kpi.sent) }}
                            <span v-if="cmp !== 'none' && st.prev" class="delta" :class="deltaClass(st.kpi.sent, st.prev.sent)">{{ deltaText(st.kpi.sent, st.prev.sent) }}</span>
                        </div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">Total minutes</div>
                        <div class="kpiVal sky">
                            {{ n(st.kpi.minutes) }}<span class="unit">min</span>
                            <span v-if="cmp !== 'none' && st.prev" class="delta" :class="deltaClass(st.kpi.minutes, st.prev.minutes)">{{ deltaText(st.kpi.minutes, st.prev.minutes) }}</span>
                        </div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">Transferred</div>
                        <div class="kpiVal warn">
                            {{ n(st.kpi.transfer) }}
                            <span v-if="cmp !== 'none' && st.prev" class="delta" :class="deltaClass(st.kpi.transfer, st.prev.transfer)">{{ deltaText(st.kpi.transfer, st.prev.transfer) }}</span>
                        </div>
                        <div class="kpiSub">{{ pct(st.kpi.transfer, st.kpi.sent) }} of total</div>
                    </div>
                    <div class="kpi">
                        <div class="kpiLabel">AI resolution</div>
                        <div class="kpiVal ok">{{ pct(st.kpi.success - st.kpi.transfer, st.kpi.sent) }}</div>
                        <div class="kpiSub">{{ st.kpi.success - st.kpi.transfer }} calls</div>
                    </div>
                </div>

                <div class="panel">
                    <div class="panelHead">
                        <h3>Call trend</h3>
                        <span class="hint">Daily · count · minutes</span>
                    </div>
                    <div v-if="st.trend && st.trend.length" class="chart">
                        <div v-for="b in st.trend" :key="b.date" class="col">
                            <span class="barNum">{{ b.sent }}<small>·{{ b.minutes }}m</small></span>
                            <div class="stack" :style="{ height: barHeight(b.sent, trendMax) + 'px' }">
                                <i class="seg ok" :style="{ height: segPct(b.success - b.transfer, b.sent) }">
                                    <em v-if="b.success - b.transfer > 0">{{ b.success - b.transfer }}</em>
                                </i>
                                <i class="seg warn" :style="{ height: segPct(b.transfer, b.sent) }">
                                    <em v-if="b.transfer > 0">{{ b.transfer }}</em>
                                </i>
                            </div>
                            <label>{{ b.date.slice(5) }}</label>
                        </div>
                    </div>
                    <div v-else class="empty">No trend data.</div>
                    <div class="legend">
                        <span><b class="okDot"></b>AI completed</span>
                        <span><b class="warnDot"></b>Transferred</span>
                    </div>
                </div>

                <div class="panel">
                    <div class="panelHead">
                        <h3>Caller topics</h3>
                        <span class="hint">From call topics</span>
                    </div>
                    <div v-if="st.keywords && st.keywords.length" class="kwList">
                        <div v-for="(k, i) in st.keywords" :key="i" class="kwRow">
                            <span class="kwLabel">{{ k[0] }}</span>
                            <div class="kwBarWrap">
                                <div class="kwBar" :style="{ width: (k[1] / kwMax * 100) + '%' }"></div>
                            </div>
                            <span class="kwVal">{{ k[1] }}</span>
                        </div>
                    </div>
                    <div v-else class="empty">No topic data.</div>
                </div>

                <div class="panel">
                    <div class="panelHead">
                        <h3>Transfer records</h3>
                        <span class="hint">Click to view AI work order</span>
                    </div>
                    <div v-if="st.transfers && st.transfers.length" class="tfList">
                        <div
                            v-for="(t, i) in st.transfers"
                            :key="t.conversation_id"
                            class="tfItem"
                            @click="toggleTf(i)"
                        >
                            <div class="tfHead">
                                <span class="tfWhen">{{ t.whenLocal }}</span>
                                <span class="tfTitle">{{ t.title || 'Call' }}</span>
                                <span class="tfDur">{{ Math.round((t.durationSecs || 0) / 60) }}m {{ (t.durationSecs || 0) % 60 }}s</span>
                                <span class="tfCaret">{{ openTf === i ? '▾' : '▸' }}</span>
                            </div>
                            <div v-if="openTf === i" class="tfReason">
                                <div class="woMeta">
                                    <div v-if="t.callerNumber"><span class="woK">Caller</span><span class="woV">{{ t.callerNumber }}</span></div>
                                    <div v-if="t.agentNumber"><span class="woK">Agent #</span><span class="woV">{{ t.agentNumber }}</span></div>
                                    <div><span class="woK">Time</span><span class="woV">{{ t.whenLocal }} ({{ t.tz }})</span></div>
                                    <div v-if="t.workOrder && t.workOrder.callType"><span class="woK">Call type</span><span class="woV">{{ t.workOrder.callType }}</span></div>
                                </div>
                                <template v-if="t.workOrder && t.workOrder.summary && t.workOrder.summary.length">
                                    <div class="woSection">
                                        <div class="woHead">📋 Reservation summary</div>
                                        <table class="woTable">
                                            <tr v-for="(row, ri) in t.workOrder.summary" :key="ri">
                                                <td class="woLabel">{{ row[0] }}</td>
                                                <td class="woData">{{ row[1] }}</td>
                                            </tr>
                                        </table>
                                    </div>
                                </template>
                                <div v-if="t.workOrder" class="woSection">
                                    <div class="woHead">💬 Other inquiries</div>
                                    <div class="woText">{{ t.workOrder.otherInquiry }}</div>
                                </div>
                                <div v-if="t.workOrder" class="woSection">
                                    <div class="woHead">📞 Human follow-up</div>
                                    <div class="woText" :class="{ woAlert: t.workOrder.needFollowup }">
                                        {{ t.workOrder.followupReason || (t.workOrder.needFollowup ? 'Follow-up needed' : 'No follow-up needed') }}
                                    </div>
                                </div>
                                <div v-if="!t.workOrder || !t.workOrder.summary || !t.workOrder.summary.length" class="woSection">
                                    <div class="woHead">📋 AI summary</div>
                                    <div class="woText">{{ t.summaryText }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty">No transfers in this period.</div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'voiceai_dashboard',
    data() {
        return {
            view: 'overview',
            range: 'week',
            pickDate: '2026-08-15',
            cmp: 'none',
            currentStoreName: '',
            ov: null,
            st: null,
            loading: false,
            error: '',
            dataLive: false,
            openTf: -1,
            ranges: [
                { id: 'today', label: 'Today' },
                { id: 'week', label: 'Last 7 days' },
                { id: 'month', label: 'Last 30 days' },
                { id: 'day', label: 'Pick a day' }
            ]
        };
    },
    computed: {
        trendMax() {
            if (!this.st || !this.st.trend || !this.st.trend.length) {
                return 1;
            }
            return Math.max.apply(null, [1].concat(this.st.trend.map(function (b) { return b.sent; })));
        },
        ovTrendMax() {
            if (!this.ov || !this.ov.trend || !this.ov.trend.length) {
                return 1;
            }
            return Math.max.apply(null, [1].concat(this.ov.trend.map(function (b) { return b.sent; })));
        },
        kwMax() {
            if (!this.st || !this.st.keywords || !this.st.keywords.length) {
                return 1;
            }
            return Math.max.apply(null, this.st.keywords.map(function (k) { return k[1]; }));
        }
    },
    created() {
        this.reload();
    },
    mounted() {
        var col = this.$el && this.$el.closest('.col_main');
        if (col) {
            col.classList.add('voiceai-overflow');
        }
    },
    beforeDestroy() {
        var col = this.$el && this.$el.closest('.col_main');
        if (col) {
            col.classList.remove('voiceai-overflow');
        }
    },
    methods: {
        ...mapActions('monitor', ['getVoiceaiOverview', 'getVoiceaiStore']),
        n(v) {
            return (v || 0).toLocaleString();
        },
        pct(a, b) {
            return b ? Math.round(a / b * 100) + '%' : '0%';
        },
        deltaClass(a, b) {
            if (this.cmp === 'none' || b == null) {
                return 'flat';
            }
            return a > b ? 'up' : a < b ? 'down' : 'flat';
        },
        deltaText(a, b) {
            if (this.cmp === 'none' || !b) {
                return '';
            }
            const p = Math.round((a - b) / b * 100);
            return (p >= 0 ? '+' : '') + p + '%';
        },
        barHeight(val, max) {
            return Math.max(4, Math.round(val / max * 160));
        },
        segPct(part, total) {
            if (!total) {
                return '0%';
            }
            return Math.round(Math.max(0, part) / total * 100) + '%';
        },
        queryParams() {
            const params = {
                range: this.range,
                compare: this.cmp
            };
            if (this.range === 'day') {
                params.date = this.pickDate || '';
            }
            return params;
        },
        setRange(r) {
            this.range = r;
            this.reload();
        },
        toggleTf(i) {
            this.openTf = this.openTf === i ? -1 : i;
        },
        async reload() {
            this.loading = true;
            this.error = '';
            this.openTf = -1;
            try {
                if (this.view === 'overview') {
                    this.ov = await this.getVoiceaiOverview(this.queryParams());
                } else {
                    this.st = await this.getVoiceaiStore(this.queryParams());
                }
                this.dataLive = true;
            } catch (e) {
                this.error = e.message || 'Request failed';
                this.dataLive = false;
            } finally {
                this.loading = false;
            }
        },
        goOverview() {
            this.view = 'overview';
            this.currentStoreName = '';
            this.reload();
        },
        openStore(s) {
            this.currentStoreName = s.name;
            this.view = 'store';
            this.reload();
        }
    }
};
</script>

<style scoped>
.voiceai-dashboard {
    --ok: #4ade80;
    --warn: #fbbf24;
    --bad: #f87171;
    --indigo: #818cf8;
    --sky: #38bdf8;
    --line: #1c2333;
    --card: #111725;
    --muted: #8b96ad;
    --faint: #6c7891;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    margin: 0 -20px;
    min-height: calc(100vh - 60px);
    background: #0b0e14;
    overflow: visible;
    color: #e7ebf3;
    font-family: 'Inter', system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.voiceai-dashboard *,
.voiceai-dashboard *::before,
.voiceai-dashboard *::after {
    box-sizing: border-box;
}
.topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 18px 28px;
    border-bottom: 1px solid var(--line);
    position: relative;
    background: #0b0e14;
    z-index: 2;
}
.brand {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    max-width: 1140px;
}
.brand-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    min-height: 38px;
}
.crumb {
    font-size: 11px;
    color: var(--faint);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1;
}
.store {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 28px;
}
.store-main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.store-name {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: #e7ebf3;
}
.backBtn {
    flex-shrink: 0;
    background: #151b2b;
    border: 1px solid #2a3550;
    color: #c7d0e3;
    border-radius: 9px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
}
.backBtn:hover {
    border-color: #6366f1;
    color: #fff;
}
.srcTag {
    flex-shrink: 0;
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 20px;
    font-weight: 600;
    line-height: 1.2;
}
.srcTag.live {
    background: #0f2018;
    color: #6fd399;
    border: 1px solid #23543a;
}
.srcTag.demo {
    background: #2a2333;
    color: #c9a0e0;
    border: 1px solid #4a3b5e;
}
.va-content {
    flex: 1;
    position: relative;
    z-index: 1;
    padding: 28px 28px 24px;
    max-width: 1140px;
    width: 100%;
    margin: 0 auto;
}
.banner {
    padding: 12px 16px;
    border-radius: 10px;
    margin-bottom: 18px;
    font-size: 13px;
    line-height: 1.5;
}
.banner.err {
    background: #2a1a1a;
    border: 1px solid #5e3b3b;
    color: #f0b0b0;
}
.banner.loading {
    background: #151b2b;
    border: 1px solid #2a3550;
    color: #a7b2c9;
}
.rangeRow {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 22px;
    flex-wrap: wrap;
}
.tabs {
    display: inline-flex;
    align-items: center;
    background: var(--card);
    border-radius: 10px;
    padding: 5px;
    border: 1px solid var(--line);
}
.tab {
    padding: 8px 16px;
    border-radius: 7px;
    border: none;
    background: transparent;
    color: var(--muted);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
}
.tab.on {
    background: #232c44;
    color: #fff;
}
.date {
    background: var(--card);
    border: 1px solid #2a3550;
    border-radius: 8px;
    color: #e7ebf3;
    padding: 7px 10px;
    font-size: 13px;
    color-scheme: dark;
}
.cmpSel {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: var(--muted);
}
.cmpSel select {
    background: var(--card);
    border: 1px solid #2a3550;
    border-radius: 8px;
    color: #e7ebf3;
    padding: 6px 9px;
    font-size: 12.5px;
    color-scheme: dark;
}
.kpiGrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 22px;
}
.kpi {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 18px;
}
.kpi.big {
    padding: 22px;
}
.kpiLabel {
    font-size: 12.5px;
    color: var(--muted);
    margin-bottom: 8px;
}
.kpiVal {
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
}
.kpi.big .kpiVal {
    font-size: 38px;
}
.kpiVal.indigo { color: var(--indigo); }
.kpiVal.ok { color: var(--ok); }
.kpiVal.warn { color: var(--warn); }
.kpiVal.sky { color: var(--sky); }
.kpiVal .unit {
    font-size: 16px;
    color: var(--faint);
    margin-left: 4px;
}
.kpiSub {
    font-size: 12px;
    color: var(--faint);
    margin-top: 4px;
}
.delta {
    font-size: 12px;
    font-weight: 700;
    margin-left: 8px;
}
.delta.up { color: var(--ok); }
.delta.down { color: var(--bad); }
.delta.flat { color: var(--faint); }
.panel {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 22px;
}
.panelHead {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 16px;
}
.panelHead h3 {
    font-size: 15px;
    font-weight: 700;
    margin: 0;
    color: #c7d0e3;
}
.hint {
    margin-left: auto;
    font-size: 12px;
    color: var(--faint);
}
table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}
thead th {
    text-align: right;
    padding: 10px 12px;
    color: var(--muted);
    font-weight: 600;
    font-size: 12px;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
}
thead th:first-child {
    text-align: left;
}
tbody td {
    padding: 11px 12px;
    text-align: right;
    border-bottom: 1px solid #161d2c;
    font-variant-numeric: tabular-nums;
}
tbody td:first-child {
    text-align: left;
}
.storeRow {
    cursor: pointer;
}
.storeRow:hover td {
    background: #141b2b;
}
.caret {
    display: inline-block;
    color: var(--faint);
    margin-right: 7px;
}
.pct {
    color: var(--faint);
    font-size: 11px;
    margin-left: 5px;
}
.drill {
    color: var(--sky);
    font-size: 11.5px;
    font-weight: 600;
}
.liveCard {
    border: 1px solid #23543a;
    background: linear-gradient(180deg, #0f2018, #0d1420);
    border-radius: 14px;
    padding: 18px;
    margin-bottom: 22px;
}
.liveHead {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 12px;
}
.dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #22c55e;
    animation: pulse 1.6s infinite;
}
.lt {
    font-weight: 700;
    font-size: 14px;
}
.lc {
    font-size: 12px;
    color: #6fd399;
}
.liveItem {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #0c1a13;
    border-radius: 9px;
    font-size: 13px;
    margin-bottom: 8px;
}
.g {
    color: #22c55e;
    font-size: 9px;
}
.el {
    color: #6fd399;
    font-weight: 600;
}
.kwList {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.kwRow {
    display: grid;
    grid-template-columns: 180px 1fr 44px;
    align-items: center;
    gap: 12px;
}
.kwLabel {
    font-size: 13px;
    color: #c7d0e3;
}
.kwBarWrap {
    height: 8px;
    background: #1a2234;
    border-radius: 5px;
    overflow: hidden;
}
.kwBar {
    height: 100%;
    background: linear-gradient(90deg, #6366f1, #0ea5e9);
    border-radius: 5px;
}
.kwVal {
    font-size: 13px;
    color: var(--muted);
    text-align: right;
    font-variant-numeric: tabular-nums;
}
.chart {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    height: 220px;
    padding: 28px 0 8px;
    overflow-x: auto;
}
.col {
    flex: 1;
    min-width: 38px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    justify-content: flex-end;
}
.barNum {
    font-size: 11px;
    font-weight: 700;
    color: #c7d0e3;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    text-align: center;
}
.barNum small {
    color: var(--faint);
    font-weight: 600;
    font-size: 10px;
    margin-left: 2px;
}
.stack {
    width: 100%;
    max-width: 40px;
    display: flex;
    flex-direction: column-reverse;
    border-radius: 5px 5px 0 0;
    overflow: hidden;
    background: #141b2b;
}
.stack .seg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
}
.stack .seg.ok {
    background: var(--ok);
}
.stack .seg.warn {
    background: var(--warn);
}
.stack .seg em {
    font-style: normal;
    font-size: 10.5px;
    font-weight: 700;
    color: #0b0e14;
}
.col label {
    font-size: 10.5px;
    color: var(--faint);
    white-space: nowrap;
}
.legend {
    display: flex;
    gap: 16px;
    margin-top: 14px;
    font-size: 12px;
    color: var(--muted);
    flex-wrap: wrap;
}
.okDot,
.warnDot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 5px;
    vertical-align: -1px;
}
.okDot {
    background: var(--ok);
}
.warnDot {
    background: var(--warn);
}
.tfList {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.tfItem {
    background: #0d1220;
    border: 1px solid var(--line);
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
}
.tfItem:hover {
    border-color: #2a3550;
}
.tfHead {
    display: grid;
    grid-template-columns: 150px 1fr auto auto;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
}
.tfWhen {
    font-size: 12px;
    color: var(--faint);
    font-variant-numeric: tabular-nums;
}
.tfTitle {
    font-size: 13px;
    color: #c7d0e3;
    font-weight: 600;
}
.tfDur {
    font-size: 12px;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
}
.tfCaret {
    color: var(--warn);
    font-size: 12px;
    width: 14px;
}
.tfReason {
    padding: 13px 14px 14px;
    border-top: 1px solid var(--line);
}
.woMeta {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 14px;
    padding: 10px 12px;
    background: #0a0e17;
    border-radius: 8px;
}
.woMeta > div {
    display: flex;
    gap: 10px;
    font-size: 12.5px;
}
.woK {
    color: var(--faint);
    width: 72px;
    flex-shrink: 0;
}
.woV {
    color: #c7d0e3;
    font-variant-numeric: tabular-nums;
}
.woSection {
    margin-bottom: 13px;
}
.woHead {
    font-size: 12.5px;
    font-weight: 700;
    color: var(--sky);
    margin-bottom: 7px;
}
.woText {
    font-size: 12.5px;
    color: #c9d3e8;
    line-height: 1.7;
    white-space: pre-wrap;
}
.woAlert {
    color: #fbbf24;
    font-weight: 600;
}
.woTable {
    width: 100%;
    border-collapse: collapse;
}
.woTable td {
    padding: 5px 8px;
    font-size: 12.5px;
    border-bottom: 1px solid #161d2c;
    vertical-align: top;
}
.woLabel {
    color: var(--muted);
    width: 110px;
    white-space: nowrap;
}
.woData {
    color: #dbe2f0;
}
.empty {
    color: var(--faint);
    font-size: 13px;
    text-align: center;
    padding: 40px;
}
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 #22c55e88; }
    70% { box-shadow: 0 0 0 8px #22c55e00; }
    100% { box-shadow: 0 0 0 0 #22c55e00; }
}
</style>

<style>
.col_main.voiceai-overflow {
    overflow: visible;
}
</style>
